import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom } from '@angular/core';
import { provideRouter } from '@angular/router';
import { LogLevel } from '@azure/msal-common';

import {
  AUTH_SERVICE_CONFIG,
  AuthInterceptorConfig,
  CelumAuthModule,
  CustomTokenEndpointInterceptor,
  SERVICE_TOKEN_INTERCEPTOR_CONFIG,
  ServiceTokenInterceptorConfig,
  TenantService
} from '@celum/authentication';
import { ROUTE_PARAM_SERVICE_CONFIG } from '@celum/ng2base/services';
import { provideCelumTranslations } from '@celum/ng2base/translation';
import { provideLocalizedDatepickerIntl } from '@celum/ng2base/util';
import { registerWidgets } from '@celum/portals/api';
import { getPortalApiConfigurations, QueryParamTokensProviderService } from '@celum/portals/shared';
import { NoopTrackingService, provideApplicationInsights, TRACKING_SERVICE } from '@celum/shared/domain';

import { ROUTES } from './app.routes';
import { PortalsAuthGuard } from './portals-auth.guard';
import { PortalsAuthInterceptor } from './portals-auth.interceptor';
import { PortalsCloudTokenInterceptor } from './portals-cloud-token.interceptor';
import { PortalsTokensInterceptor } from './portals-tokens.interceptor';

// make sure that basic information for available widgets are ready once the portal component is initialized
registerWidgets();

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(ROUTES),
    provideCelumTranslations(),
    importProvidersFrom(
      CelumAuthModule.forRoot({
        logLevel: LogLevel.Error
      })
    ),
    provideLocalizedDatepickerIntl(),
    QueryParamTokensProviderService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PortalsAuthInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PortalsTokensInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CustomTokenEndpointInterceptor,
      multi: true
    },
    {
      provide: SERVICE_TOKEN_INTERCEPTOR_CONFIG,
      useFactory: serviceTokenInterceptorFactory,
      deps: [TenantService]
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PortalsCloudTokenInterceptor,
      multi: true
    },
    {
      provide: AUTH_SERVICE_CONFIG,
      useValue: { loadSaccUserAfterSignIn: false }
    },
    PortalsAuthGuard,
    {
      provide: TRACKING_SERVICE,
      useFactory: () => {
        const searchParams = new URLSearchParams(window.location.search);
        const isWorkReviewMode = searchParams.has('b2cToken') && searchParams.has('portalReadToken');

        return isWorkReviewMode ? new NoopTrackingService() : provideApplicationInsights().useFactory();
      }
    },
    {
      provide: ROUTE_PARAM_SERVICE_CONFIG,
      useValue: { preserveFragment: true }
    },
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false }
    }
  ]
};

function serviceTokenInterceptorFactory(tenantService: TenantService): ServiceTokenInterceptorConfig<AuthInterceptorConfig> {
  const getConfiguration = () => {
    const tenantIdForPortal = tenantService.getCurrentTenantId();

    const apiConfigurations = getPortalApiConfigurations(tenantIdForPortal);

    return {
      apiConfigurations,
      passThrough403Error: true
    };
  };

  return {
    getInterceptorConfiguration: () => getConfiguration()
  };
}
